import {
  CompsListingsSearchCriteria,
  GeoSearch,
  MILES_TO_METERS,
  SubjectData,
} from '@aloft/toolkit-shared';

import { EMPTY_RANGE_FILTER } from '../internal/FilterPanel/FilterSection.tsx';
import { InitialCompSearch } from '../types.ts';
import { replaceUndefinedWithEmptyString } from './compSearchUtils.ts';
import { DEFAULT_RECOMMENDED_COMP_PROPERTY_SUB_TYPE } from './getDefaultRecommendedCompSearch.ts';

const mergeFilterField = <T extends Record<string, unknown>>(
  existing: T | undefined,
  defaultFilter: T | undefined,
): T => {
  if (existing !== undefined) {
    return replaceUndefinedWithEmptyString(existing);
  }
  return replaceUndefinedWithEmptyString(defaultFilter ?? ({} as T));
};

const MIN_MAX_BUTTON_FIELDS = [
  'bedrooms',
  'fullBaths',
  'halfBaths',
  'garageSpaces',
  'quality',
  'condition',
] as const;

type MinMaxButtonField = (typeof MIN_MAX_BUTTON_FIELDS)[number];

const mergeMinMaxButtonFilter = <T extends Record<string, unknown>>(
  existing: T | undefined,
  defaultFilter: T | undefined,
  matchCriteria: string | undefined,
  subjectCenter?: number,
): T => {
  const merged = mergeFilterField(existing, defaultFilter);

  if (matchCriteria === 'exact' && typeof merged.exact === 'number') {
    return replaceUndefinedWithEmptyString({
      ...EMPTY_RANGE_FILTER,
      min: merged.exact,
      max: merged.exact,
    }) as unknown as T;
  }

  if (matchCriteria === 'value_range') {
    const center = typeof merged.exact === 'number' ? merged.exact : subjectCenter;
    if (typeof center === 'number' && typeof merged.variance === 'number') {
      return replaceUndefinedWithEmptyString({
        ...EMPTY_RANGE_FILTER,
        min: Math.max(0, center - merged.variance),
        max: center + merged.variance,
      }) as unknown as T;
    }
    return replaceUndefinedWithEmptyString({ ...EMPTY_RANGE_FILTER }) as unknown as T;
  }

  return replaceUndefinedWithEmptyString({
    ...EMPTY_RANGE_FILTER,
    min: merged.min ?? '',
    max: merged.max ?? '',
  }) as unknown as T;
};

export const loadExistingSearchCriteria = (
  existingSearchCriteria: CompsListingsSearchCriteria,
  subject: SubjectData,
  getDefaultFormValues: () => InitialCompSearch['body']['search'],
): InitialCompSearch['body']['search'] => {
  const defaultSearch = getDefaultFormValues();
  const existingFilters = existingSearchCriteria.filters;
  const existingMatchCriteria = existingSearchCriteria.matchCriteria;
  const { improvements } = subject.editableData;
  const listing = subject.readonlyData?.backupListingData?.listing;
  const subjectCoords = {
    lon: subject.editableData.propertyIdentification.coordinates?.longitude || 0,
    lat: subject.editableData.propertyIdentification.coordinates?.latitude || 0,
  };
  let geoSearch: GeoSearch;
  if (existingSearchCriteria.coordinates && existingSearchCriteria.coordinates.length > 2) {
    geoSearch = {
      t: 'polygon',
      coords: existingSearchCriteria.coordinates.map((coordinate) => [
        coordinate.longitude,
        coordinate.latitude,
      ]),
    };
  } else {
    geoSearch = {
      t: 'radius',
      radiusInMeters: existingSearchCriteria.radius
        ? existingSearchCriteria.radius * MILES_TO_METERS
        : defaultSearch.geoSearch.t === 'radius'
          ? defaultSearch.geoSearch.radiusInMeters
          : MILES_TO_METERS,
      coord: [subjectCoords.lon, subjectCoords.lat],
    };
  }

  const subjectCenters: Partial<Record<MinMaxButtonField, number | undefined>> = {
    bedrooms: improvements.bedrooms ?? listing?.BedroomsTotal,
    fullBaths: improvements.fullBaths ?? listing?.BathroomsFull,
    halfBaths: improvements.halfBaths ?? listing?.BathroomsHalf,
    garageSpaces: improvements.garage?.spaces,
    quality: improvements.uadQuality,
    condition: improvements.uadCondition,
  };

  const minMaxFilters = Object.fromEntries(
    MIN_MAX_BUTTON_FIELDS.map((field) => [
      field,
      mergeMinMaxButtonFilter(
        existingFilters?.[field],
        defaultSearch.filters?.[field],
        existingMatchCriteria?.[field],
        subjectCenters[field],
      ),
    ]),
  ) as unknown as Record<MinMaxButtonField, Record<string, unknown>>;

  const search = {
    filters: {
      ...defaultSearch.filters,
      ...existingFilters,
      status: existingFilters?.status?.trim() ?? '',
      propertySubType:
        existingFilters?.propertySubType && existingFilters.propertySubType.length > 0
          ? existingFilters.propertySubType
          : [DEFAULT_RECOMMENDED_COMP_PROPERTY_SUB_TYPE],
      saleDate: mergeFilterField(existingFilters?.saleDate, defaultSearch.filters?.saleDate),
      salePrice: mergeFilterField(existingFilters?.salePrice, defaultSearch.filters?.salePrice),
      gla: mergeFilterField(existingFilters?.gla, defaultSearch.filters?.gla),
      lotSize: mergeFilterField(existingFilters?.lotSize, defaultSearch.filters?.lotSize),
      yearBuilt: mergeFilterField(existingFilters?.yearBuilt, defaultSearch.filters?.yearBuilt),
      stories: mergeFilterField(existingFilters?.stories, defaultSearch.filters?.stories),
      ...minMaxFilters,
    },
    geoSearch,
    matchCriteria: {
      ...defaultSearch.matchCriteria,
      ...existingMatchCriteria,
      bedrooms: 'min_max_range',
      fullBaths: 'min_max_range',
      halfBaths: 'min_max_range',
      garageSpaces: 'min_max_range',
      quality: 'min_max_range',
      condition: 'min_max_range',
    },
  };

  return search as InitialCompSearch['body']['search'];
};
